import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import { fetchCycleBoard, } from '@/entities/agent';
import { fetchBrandManagers } from '@/entities/brand-manager';
import { BoardFilterField, BoardFiltersToolbar, BOARD_SORT_OPTIONS, boardFiltersActive, boardReturnToParam, clearBoardFilterParams, matchesBoardFilters, parseBoardFilters, parseBoardSort, sortBoardItems, writeBoardFilters, writeBoardSort, } from '@/features/board-filters';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, HelpTip, IconButton, MetaStrip, RangeInputs, SearchField, Select, Skeleton, StageTabs, } from '@/shared/ui';
import { AgentScopeToggle } from '@/widgets/agent-scope-toggle';
import { CycleBoardCardView } from './cycle-board-card';
import { CycleBoardTable } from './cycle-board-table';
const VIEWS = [
    { code: 'bt', title: 'БТ-Встреча' },
    { code: 'profile', title: 'Заполненность' },
    { code: 'contract', title: 'Договор с АН' },
];
function parseView(raw) {
    if (raw === 'profile' || raw === 'contract' || raw === 'bt')
        return raw;
    return 'bt';
}
function pluralEngagements(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'Вовлечение';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return 'Вовлечения';
    }
    return 'Вовлечений';
}
function pluralEngagementsLower(n) {
    return pluralEngagements(n).toLocaleLowerCase('ru');
}
export function CycleBoard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { scope } = useAgentScope();
    const view = parseView(searchParams.get('view'));
    const stageFilter = searchParams.get('stage')?.trim() || 'all';
    const filters = useMemo(() => parseBoardFilters(searchParams), [searchParams]);
    const sort = useMemo(() => parseBoardSort(searchParams), [searchParams]);
    const [title, setTitle] = useState('Вовлечения');
    const [columns, setColumns] = useState([]);
    const [items, setItems] = useState([]);
    const [stageCounts, setStageCounts] = useState([]);
    const [totals, setTotals] = useState({
        engagements: 0,
        leads: 0,
        meetings: 0,
    });
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [managers, setManagers] = useState([]);
    const [draftQ, setDraftQ] = useState(filters.q);
    const [ratingHelpOpen, setRatingHelpOpen] = useState(false);
    const hasBoardDataRef = useRef(false);
    const currentManagerId = managers.find((item) => item.isCurrent)?.managerId ?? null;
    useEffect(() => {
        setDraftQ(filters.q);
    }, [filters.q]);
    useEffect(() => {
        if (draftQ.trim() === filters.q)
            return;
        const timer = window.setTimeout(() => {
            setSearchParams(writeBoardFilters(searchParams, { ...filters, q: draftQ.trim() }), { replace: true });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [draftQ, filters, searchParams, setSearchParams]);
    const load = useCallback(async () => {
        setError(null);
        if (!hasBoardDataRef.current)
            setLoading(true);
        try {
            const board = await fetchCycleBoard({
                view,
                scope,
                status: filters.status,
            });
            setTitle(board.title);
            setColumns(board.columns);
            const flat = board.items ?? board.columns.flatMap((column) => column.cards);
            setItems(flat);
            setStageCounts(board.stageCounts ??
                board.columns.map((column) => ({
                    stageCode: column.stageCode,
                    title: column.title,
                    count: column.cards.length,
                })));
            setTotals(board.totals ?? {
                engagements: board.total,
                leads: flat.reduce((sum, row) => sum + (row.leadCount ?? 0), 0),
                meetings: flat.reduce((sum, row) => sum + (row.meetingCount ?? 0), 0),
            });
            setTotal(board.total);
            hasBoardDataRef.current = true;
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
            setColumns([]);
            setItems([]);
            setStageCounts([]);
            setTotals({ engagements: 0, leads: 0, meetings: 0 });
            setTotal(0);
            hasBoardDataRef.current = false;
        }
        finally {
            setLoading(false);
        }
    }, [scope, view, filters.status]);
    useEffect(() => {
        if (!searchParams.get('view')) {
            const next = new URLSearchParams(searchParams);
            next.set('view', 'bt');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);
    // Refetch only when API inputs change (view / scope / status), not on sort/q/ratings.
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        let cancelled = false;
        void fetchBrandManagers()
            .then((data) => {
            if (!cancelled)
                setManagers(data.items);
        })
            .catch(() => {
            if (!cancelled)
                setManagers([]);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const agencies = useMemo(() => {
        const names = new Set();
        for (const card of items) {
            const name = card.agencyName?.trim();
            if (name)
                names.add(name);
        }
        return [...names]
            .sort((a, b) => a.localeCompare(b, 'ru'))
            .map((name) => ({ value: name, label: name }));
    }, [items]);
    const filteredItems = useMemo(() => {
        let rows = items.filter((card) => matchesBoardFilters(card, filters, currentManagerId, {
            applyStatus: false,
        }));
        if (stageFilter !== 'all') {
            rows = rows.filter((card) => card.stageCode === stageFilter);
        }
        return sortBoardItems(rows, sort === 'default' ? 'age-desc' : sort);
    }, [items, filters, currentManagerId, stageFilter, sort]);
    const filteredStageCounts = useMemo(() => {
        const byStage = new Map();
        for (const card of items) {
            if (!matchesBoardFilters(card, filters, currentManagerId, {
                applyStatus: false,
            })) {
                continue;
            }
            byStage.set(card.stageCode, (byStage.get(card.stageCode) ?? 0) + 1);
        }
        return stageCounts.map((row) => ({
            ...row,
            count: byStage.get(row.stageCode) ?? 0,
        }));
    }, [items, filters, currentManagerId, stageCounts]);
    const filteredTotals = useMemo(() => {
        const base = items.filter((card) => matchesBoardFilters(card, filters, currentManagerId, {
            applyStatus: false,
        }));
        const scoped = stageFilter === 'all'
            ? base
            : base.filter((card) => card.stageCode === stageFilter);
        return {
            engagements: scoped.length,
            leads: scoped.reduce((sum, row) => sum + (row.leadCount ?? 0), 0),
            meetings: scoped.reduce((sum, row) => sum + (row.meetingCount ?? 0), 0),
        };
    }, [items, filters, currentManagerId, stageFilter]);
    const visibleColumns = useMemo(() => {
        return columns.map((column) => {
            const cards = sortBoardItems(column.cards.filter((card) => matchesBoardFilters(card, filters, currentManagerId, {
                applyStatus: false,
            })), sort === 'default' ? 'age-desc' : sort);
            return { ...column, cards };
        });
    }, [columns, filters, currentManagerId, sort]);
    const visibleTotal = useMemo(() => visibleColumns.reduce((sum, column) => sum + column.cards.length, 0), [visibleColumns]);
    function selectView(code) {
        const next = new URLSearchParams(searchParams);
        next.set('view', code);
        next.delete('trajectory');
        next.delete('stage');
        setSearchParams(next);
    }
    function applyFilters(nextFilters) {
        setSearchParams(writeBoardFilters(searchParams, nextFilters), {
            replace: true,
        });
    }
    function applySort(nextSort) {
        setSearchParams(writeBoardSort(searchParams, nextSort), { replace: true });
    }
    function applyStage(stageCode) {
        const next = new URLSearchParams(searchParams);
        if (stageCode === 'all')
            next.delete('stage');
        else
            next.set('stage', stageCode);
        setSearchParams(next, { replace: true });
    }
    function resetFilters() {
        const next = clearBoardFilterParams(searchParams);
        if (!next.get('view'))
            next.set('view', view);
        next.delete('stage');
        setSearchParams(next, { replace: true });
    }
    function openCycle(displayNumber) {
        const returnTo = boardReturnToParam(`/engagements?${searchParams.toString()}`);
        navigate(`/engagements/cycles/${encodeURIComponent(displayNumber)}?returnTo=${returnTo}`);
    }
    function patchRating(key, value) {
        applyFilters({ ...filters, [key]: value });
    }
    const stageTabItems = useMemo(() => {
        const allCount = filteredStageCounts.reduce((sum, row) => sum + row.count, 0);
        const allTab = {
            id: 'all',
            label: 'Общий список',
            count: allCount,
        };
        if (view === 'contract') {
            const order = [
                'FILL_AGENCY',
                'SIGN',
                'HAS_CONTRACT',
                'NEEDS_AGENCY_REVIEW',
            ];
            const byCode = new Map(filteredStageCounts.map((row) => [row.stageCode, row]));
            const ordered = order
                .map((code) => byCode.get(code))
                .filter((row) => row != null)
                .map((row) => ({
                id: row.stageCode,
                label: row.title,
                count: row.count,
                tone: row.stageCode === 'NEEDS_AGENCY_REVIEW'
                    ? 'review'
                    : undefined,
            }));
            return [...ordered, allTab];
        }
        return [
            allTab,
            ...filteredStageCounts.map((row) => ({
                id: row.stageCode,
                label: row.title,
                count: row.count,
            })),
        ];
    }, [filteredStageCounts, view]);
    const responsibleOptions = useMemo(() => [
        { value: 'all', label: 'Все ответственные' },
        { value: 'none', label: 'Без закрепления' },
        ...managers.map((manager) => ({
            value: manager.managerId,
            label: `${manager.displayName}${manager.isCurrent ? ' (я)' : ''}`,
        })),
    ], [managers]);
    const agencyOptions = useMemo(() => [
        { value: 'all', label: 'Все агентства' },
        { value: 'none', label: 'Без АН' },
        ...agencies,
    ], [agencies]);
    const sortSelectValue = sort === 'default' ? 'age-desc' : sort;
    const workControlsActive = boardFiltersActive(filters) ||
        sort !== 'default' ||
        stageFilter !== 'all';
    const isWorkTable = view === 'bt' || view === 'contract';
    const isProfile = view === 'profile';
    return (_jsxs("div", { className: isWorkTable ? 'space-y-3 px-0 py-1' : 'space-y-4 px-4 py-4 md:px-6', children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("h1", { className: "text-xl font-semibold", children: title }), isProfile ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "\u041A\u0430\u0440\u0442\u043E\u0447\u043A\u0430 \u2014 \u0446\u0438\u043A\u043B \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u044F. \u042D\u0442\u0430\u043F \u0441\u0447\u0438\u0442\u0430\u0435\u0442\u0441\u044F \u0438\u0437 CRM; \u043F\u0435\u0440\u0435\u0442\u0430\u0441\u043A\u0438\u0432\u0430\u043D\u0438\u0435 \u0437\u0430\u043A\u0440\u044B\u0442\u043E." })) : null, isProfile ? (_jsxs("p", { className: "text-sm text-muted-foreground", children: ["\u041D\u0430 \u0434\u043E\u0441\u043A\u0435: ", visibleTotal, visibleTotal !== total ? ` из ${total}` : '', ".", ' ', _jsx(Link, { className: "text-primary hover:underline", to: "/engagements/new", children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0435" }), ' · ', _jsx(Link, { className: "text-primary hover:underline", to: "/engagements/projects", children: "\u041F\u043E\u0441\u0435\u0449\u0435\u043D\u043D\u043E\u0441\u0442\u044C \u043F\u0440\u043E\u0435\u043A\u0442\u0430" })] })) : null] }), _jsx(AgentScopeToggle, {})] }), _jsxs("nav", { "aria-label": "\u041F\u0440\u0435\u0434\u0441\u0442\u0430\u0432\u043B\u0435\u043D\u0438\u044F \u0432\u043E\u0440\u043E\u043D\u043E\u043A", className: "flex flex-wrap gap-2", children: [VIEWS.map((item) => (_jsx(Button, { type: "button", size: "sm", variant: item.code === view ? 'default' : 'outline', onClick: () => selectView(item.code), children: item.title }, item.code))), _jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => navigate('/engagements/projects'), children: "\u041F\u043E\u0441\u0435\u0449\u0435\u043D\u043D\u043E\u0441\u0442\u044C \u043F\u0440\u043E\u0435\u043A\u0442\u0430" })] }), isWorkTable ? (_jsxs(_Fragment, { children: [_jsx(StageTabs, { items: stageTabItems, value: stageFilter, onValueChange: applyStage }), _jsxs("div", { className: "flex flex-wrap items-center gap-2 rounded-[var(--unity-radius)] border border-border bg-[var(--unity-color-elevated)] px-3 py-2", children: [_jsxs("div", { className: "text-[length:var(--unity-work-compact-font-size,0.875rem)] italic text-muted-foreground", children: [_jsx("strong", { className: "not-italic text-foreground", children: filteredTotals.engagements }), ' ', pluralEngagementsLower(filteredTotals.engagements)] }), _jsx(Button, { type: "button", size: "sm", onClick: () => navigate('/engagements/new'), children: "+ \u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0435" }), _jsx(SearchField, { className: "min-w-[14rem] flex-1", value: draftQ, onValueChange: setDraftQ, placeholder: "\u0410\u0433\u0435\u043D\u0442, \u0442\u0435\u043B\u0435\u0444\u043E\u043D, \u2116 \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u044F", "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0439" }), _jsx(Select, { size: "compact", className: "min-w-[10rem]", value: filters.responsible, onValueChange: (value) => applyFilters({ ...filters, responsible: value }), options: responsibleOptions, "aria-label": "\u041E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0435\u043D\u043D\u044B\u0439" }), _jsx(Select, { size: "compact", className: "min-w-[10rem]", value: filters.agency, onValueChange: (value) => applyFilters({ ...filters, agency: value }), options: agencyOptions, "aria-label": "\u0410\u0433\u0435\u043D\u0442\u0441\u0442\u0432\u043E" }), _jsx(Select, { size: "compact", className: "min-w-[8rem]", value: filters.status, onValueChange: (value) => applyFilters({
                                    ...filters,
                                    status: value,
                                }), options: [
                                    { value: 'active', label: 'Активные' },
                                    { value: 'closed', label: 'Завершённые' },
                                    { value: 'all', label: 'Все статусы' },
                                ], "aria-label": "\u0421\u0442\u0430\u0442\u0443\u0441" })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[var(--unity-radius)] border border-border bg-[color-mix(in_srgb,var(--unity-color-page)_70%,white)] px-3 py-1.5 text-[11px] text-muted-foreground", children: [_jsxs("label", { className: "flex items-center gap-1.5 whitespace-nowrap", children: [_jsx("span", { children: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u043A\u0430" }), _jsx(Select, { size: "compact", className: "min-w-[12rem]", value: sortSelectValue, onValueChange: (next) => applySort(next), options: BOARD_SORT_OPTIONS, "aria-label": "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u043A\u0430 \u0412\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0439" })] }), _jsx(RangeInputs, { label: "\u0420. \u0410\u0433\u0435\u043D\u0442\u0430", title: "\u0420\u0435\u0439\u0442\u0438\u043D\u0433 \u0410\u0433\u0435\u043D\u0442\u0430 \u0437\u0430 \u0432\u0441\u0451 \u0432\u0440\u0435\u043C\u044F", minValue: filters.ratingMin, maxValue: filters.ratingMax, onMinChange: (value) => patchRating('ratingMin', value), onMaxChange: (value) => patchRating('ratingMax', value) }), _jsx(RangeInputs, { label: "\u0420. \u0412\u043E\u0432\u043B.", title: "\u0420\u0435\u0439\u0442\u0438\u043D\u0433 \u0412\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u044F", minValue: filters.engagementMin, maxValue: filters.engagementMax, onMinChange: (value) => patchRating('engagementMin', value), onMaxChange: (value) => patchRating('engagementMax', value) }), _jsx(RangeInputs, { label: "\u0412\u043D\u0435\u0448\u043D\u0438\u0439", title: "\u0412\u043D\u0435\u0448\u043D\u0438\u0439 \u0440\u0435\u0439\u0442\u0438\u043D\u0433", minValue: filters.externalMin, maxValue: filters.externalMax, onMinChange: (value) => patchRating('externalMin', value), onMaxChange: (value) => patchRating('externalMax', value) }), workControlsActive ? (_jsx("button", { type: "button", className: "text-primary hover:underline", onClick: resetFilters, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" })) : null, _jsx(HelpTip, { open: ratingHelpOpen, onOpenChange: setRatingHelpOpen, align: "end", title: "\u0420\u0435\u0439\u0442\u0438\u043D\u0433\u0438", trigger: _jsx(IconButton, { size: "sm", pressed: ratingHelpOpen, "aria-label": "\u0421\u043F\u0440\u0430\u0432\u043A\u0430 \u043F\u043E \u0440\u0435\u0439\u0442\u0438\u043D\u0433\u0430\u043C", "aria-expanded": ratingHelpOpen, children: _jsx(Info, { "aria-hidden": true }) }), children: _jsx("p", { children: "\u0420\u0410 \u2014 \u0440\u0435\u0439\u0442\u0438\u043D\u0433 \u0430\u0433\u0435\u043D\u0442\u0430 \u0437\u0430 \u0432\u0441\u0451 \u0432\u0440\u0435\u043C\u044F. \u0420\u0412 \u2014 \u0440\u0435\u0439\u0442\u0438\u043D\u0433 \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u044F \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434 \u0446\u0438\u043A\u043B\u0430. \u0412\u0420 \u2014 \u0432\u043D\u0435\u0448\u043D\u0438\u0439 \u0440\u0435\u0439\u0442\u0438\u043D\u0433 \u0438\u0437 \u043F\u0440\u0438\u043E\u0440\u0438\u0442\u0435\u0442\u043D\u043E\u0433\u043E \u0441\u043F\u0438\u0441\u043A\u0430. \u0414\u0438\u0430\u043F\u0430\u0437\u043E\u043D\u044B \u0441\u043E\u0447\u0435\u0442\u0430\u044E\u0442\u0441\u044F \u0447\u0435\u0440\u0435\u0437 \u0418." }) })] }), view === 'bt' ? (_jsx(MetaStrip, { primary: `${filteredTotals.engagements} ${pluralEngagements(filteredTotals.engagements)}`, items: [
                            _jsxs(_Fragment, { children: ["\u041B\u0438\u0434\u044B: ", filteredTotals.leads] }),
                            _jsxs(_Fragment, { children: ["\u041F\u0440\u043E\u0432\u0435\u0434\u0451\u043D\u043D\u044B\u0435 \u0432\u0441\u0442\u0440\u0435\u0447\u0438: ", filteredTotals.meetings] }),
                            ...(filteredTotals.engagements !== totals.engagements
                                ? [_jsxs(_Fragment, { children: ["(\u0438\u0437 ", total, " \u043D\u0430 \u0434\u043E\u0441\u043A\u0435)"] })]
                                : []),
                        ] })) : null] })) : (_jsx(BoardFiltersToolbar, { mode: "board", filters: filters, managers: managers, agencies: agencies, onChange: applyFilters, onReset: resetFilters, extraActive: sort !== 'default', trailing: _jsx(BoardFilterField, { label: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u043A\u0430", htmlFor: "board-sort", children: _jsx(Select, { id: "board-sort", className: "min-w-[12rem]", value: sort === 'default' ? 'default' : sort, onValueChange: (next) => applySort(next), options: [
                            { value: 'default', label: 'По умолчанию' },
                            { value: 'age-desc', label: 'Дольше на этапе' },
                            { value: 'age-asc', label: 'Короче на этапе' },
                        ] }) }) })), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, loading ? (isWorkTable ? (_jsx(Skeleton, { className: "h-64 w-full" })) : (_jsx("div", { className: "flex gap-3 overflow-x-auto pb-2", children: Array.from({ length: 4 }).map((_, index) => (_jsx(Skeleton, { className: "h-64 w-64 shrink-0" }, index))) }))) : isWorkTable ? (_jsx(CycleBoardTable, { rows: filteredItems, sort: sortSelectValue, onSortChange: applySort, onOpenCycle: openCycle, variant: view === 'contract' ? 'contract' : 'bt' })) : (_jsx("div", { className: "flex gap-3 overflow-x-auto pb-2", children: visibleColumns.map((column) => (_jsxs("section", { className: "flex w-64 shrink-0 flex-col gap-2 rounded-md border border-border bg-muted/30 p-2", "aria-label": column.title, children: [_jsxs("header", { className: "flex items-center justify-between gap-2 px-1", children: [_jsx("h2", { className: "text-sm font-medium", children: column.title }), _jsx("span", { className: "text-xs text-muted-foreground", children: column.cards.length })] }), column.cards.length === 0 ? (_jsx("p", { className: "px-1 text-xs text-muted-foreground", children: "\u041F\u0443\u0441\u0442\u043E" })) : (_jsx("ul", { className: "flex flex-col gap-2", children: column.cards.map((card) => (_jsx(CycleBoardCardView, { card: card, onOpenCycle: () => openCycle(card.displayNumber) }, card.agentEngagementId))) }))] }, column.stageCode))) }))] }));
}
